import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { MailPlus } from "lucide-react";

export default function CreateRequestModal({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ client_name: "", iso_standard: "ISO 9001", request_type: "GAP Analysis", urgency: "Normal", note: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) return toast.error("Client name is required");
    setSaving(true);
    try {
      await endpoints.createRequest(form);
      toast.success("Request created");
      onCreated?.();
      onOpenChange(false);
      setForm({ client_name: "", iso_standard: "ISO 9001", request_type: "GAP Analysis", urgency: "Normal", note: "" });
    } catch {
      toast.error("Could not save request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-create-request" className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-500/20">
              <MailPlus className="h-4.5 w-4.5" />
            </div>
            <div>
              <DialogTitle>Create Request</DialogTitle>
              <DialogDescription>Capture a new client request.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="req-client">Client name</Label>
            <Input data-testid="request-client-input" id="req-client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="e.g. Northwind Corp" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>ISO standard</Label>
              <Select value={form.iso_standard} onValueChange={(v) => setForm({ ...form, iso_standard: v })}>
                <SelectTrigger data-testid="request-standard-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ISO 9001">ISO 9001</SelectItem>
                  <SelectItem value="ISO 14001">ISO 14001</SelectItem>
                  <SelectItem value="ISO 27001">ISO 27001</SelectItem>
                  <SelectItem value="ISO 45001">ISO 45001</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Request type</Label>
              <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                <SelectTrigger data-testid="request-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="GAP Analysis">GAP Analysis</SelectItem>
                  <SelectItem value="Document Verification">Document Verification</SelectItem>
                  <SelectItem value="Initial Consultation">Initial Consultation</SelectItem>
                  <SelectItem value="Audit Review">Audit Review</SelectItem>
                  <SelectItem value="Certification">Certification</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Urgency</Label>
            <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
              <SelectTrigger data-testid="request-urgency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Normal">Normal</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="req-note">Note (optional)</Label>
            <Textarea data-testid="request-note-input" id="req-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="request-submit-button" type="submit" disabled={saving} className="brand-gradient text-white">
              {saving ? "Saving..." : "Create request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
